<template>
  <div>
    <b-card-title class="mb-2">
      {{ $t('settings.coordinates.validation.title') }}
    </b-card-title>
    <b-card-text class="mb-3 text-muted">
      {{ $t('settings.coordinates.validation.description') }}
    </b-card-text>

    <b-form @submit.prevent="handleSubmit">
      <!-- Validation Settings -->
      <h5 class="mb-2">
        {{ $t('settings.coordinates.validation.settings') }}
      </h5>
      <b-row>
        <!-- Enable Bounds Check -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.validation.enable_bounds_check')"
            label-for="enable-bounds-check"
            :description="$t('settings.coordinates.validation.enable_bounds_check_desc')"
          >
            <b-form-checkbox
              id="enable-bounds-check"
              v-model="form.validation.enable_bounds_check"
              switch
              size="lg"
            >
              {{ form.validation.enable_bounds_check ? $t('common.enabled') : $t('common.disabled') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <!-- Coordinate Precision -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.validation.coordinate_precision')"
            label-for="coordinate-precision"
            :description="$t('settings.coordinates.validation.coordinate_precision_desc')"
          >
            <b-form-input
              id="coordinate-precision"
              v-model.number="form.validation.coordinate_precision"
              type="number"
              min="1"
              max="15"
              step="1"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Display Settings -->
      <h5 class="mt-3 mb-2">
        {{ $t('settings.coordinates.display.settings') }}
      </h5>
      <b-row>
        <!-- Display Format -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.display.format')"
            label-for="display-format"
            :description="$t('settings.coordinates.display.format_desc')"
          >
            <b-form-select
              id="display-format"
              v-model="form.display.format"
              :options="formatOptions"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Default System Settings -->
      <h5 class="mt-3 mb-2">
        {{ $t('settings.coordinates.defaults.title') }}
      </h5>
      <b-row>
        <!-- Default System -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.defaults.system')"
            label-for="default-system"
            :description="$t('settings.coordinates.defaults.system_desc')"
          >
            <b-form-select
              id="default-system"
              v-model="form.default_system"
              :options="systemOptions"
              required
            />
          </b-form-group>
        </b-col>

        <!-- Default Datum -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            :label="$t('settings.coordinates.defaults.datum')"
            label-for="default-datum"
            :description="$t('settings.coordinates.defaults.datum_desc')"
          >
            <b-form-select
              id="default-datum"
              v-model="form.default_datum"
              :options="datumOptions"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Actions -->
      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          :disabled="loading"
          @click="resetForm"
        >
          {{ $t('common.reset') }}
        </b-button>
        <b-button
          variant="primary"
          type="submit"
          :disabled="loading"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-50"
          />
          {{ $t('common.save') }}
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import {
  BCardTitle,
  BCardText,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormCheckbox,
  BRow,
  BCol,
  BButton,
  BSpinner,
} from 'bootstrap-vue'

export default {
  name: 'ValidationSettingsForm',
  components: {
    BCardTitle,
    BCardText,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BRow,
    BCol,
    BButton,
    BSpinner,
  },
  props: {
    groupedSettings: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        validation: {
          enable_bounds_check: true,
          coordinate_precision: 8,
        },
        display: {
          format: 'decimal',
        },
        default_system: 'WGS84',
        default_datum: 'WGS84',
      },
      formatOptions: [
        { value: 'decimal', text: this.$t('settings.coordinates.display.format_decimal') },
        { value: 'dms', text: this.$t('settings.coordinates.display.format_dms') },
        { value: 'utm', text: this.$t('settings.coordinates.display.format_utm') },
        { value: 'etm', text: this.$t('settings.coordinates.display.format_etm') },
      ],
      systemOptions: [
        { value: 'WGS84', text: 'WGS84' },
        { value: 'UTM', text: 'UTM' },
        { value: 'ETM', text: 'ETM' },
      ],
      datumOptions: [
        { value: 'WGS84', text: 'WGS84' },
        { value: 'NAD83', text: 'NAD83' },
        { value: 'ETRS89', text: 'ETRS89' },
      ],
    }
  },
  watch: {
    groupedSettings: {
      immediate: true,
      deep: true,
      handler(newSettings) {
        if (newSettings && Object.keys(newSettings).length > 0) {
          if (newSettings.validation) {
            this.form.validation = { ...this.form.validation, ...newSettings.validation }
          }
          if (newSettings.display) {
            this.form.display = { ...this.form.display, ...newSettings.display }
          }
          if (newSettings.default_system) {
            this.form.default_system = newSettings.default_system
          }
          if (newSettings.default_datum) {
            this.form.default_datum = newSettings.default_datum
          }
        }
      },
    },
  },
  methods: {
    handleSubmit() {
      this.$emit('save', { ...this.form })
    },
    resetForm() {
      if (this.groupedSettings && Object.keys(this.groupedSettings).length > 0) {
        const {
          validation, display, default_system, default_datum,
        } = this.groupedSettings
        this.form = {
          validation: validation || this.form.validation,
          display: display || this.form.display,
          default_system: default_system || this.form.default_system,
          default_datum: default_datum || this.form.default_datum,
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.mr-50 {
  margin-right: 0.5rem;
}
</style>
